import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import productApi from '../../services/productApi';
import { useFilter } from '../../contexts/FilterContext';
import {
    buildInitialScenarioData,
    buildInventoryFromScenarioData,
    buildProductionRunsPayload,
    buildTargetProductionRunsPayload,
    getAvailableConcentrateKegs,
    getProductTotalRevenue,
    getProductTargetRevenue,
    getRemainingKegsForFormat,
    getTargetKegsNeeded,
    getTotalAllocatedKegs,
    recalculateScenarioCapacity
} from './scenarioCalculations';
import { FORMAT_DEFINITIONS } from './constants';

// The backend's insufficient-inventory response is a structured object
// ({message, error_code, insufficient_ingredients}), not a plain string —
// interpolating it directly into a template literal would print
// "[object Object]" instead of the actual bottleneck.
function describePlanCreationError(error) {
    const detail = error.response?.data?.detail;
    if (!detail) return error.message;
    if (typeof detail === 'string') return detail;

    const shortages = (detail.insufficient_ingredients || [])
        .map((i) => `${i.ingredient_name} (needs ${i.required_quantity}, only ${i.available_quantity} available)`)
        .join('; ');
    return shortages ? `${detail.message} — ${shortages}` : (detail.message || error.message);
}

export const useScenarioPlannerWithRevenue = () => {
    const { selectedProductIds } = useFilter();
    const [originalData, setOriginalData] = useState(null);
    const [scenarioInventory, setScenarioInventory] = useState({});
    const [scenarioCapacity, setScenarioCapacity] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [activeProductId, setActiveProductId] = useState(null);
    const [activeFormatFamilyByProduct, setActiveFormatFamilyByProduct] = useState({});
    const [focusedFormatByProduct, setFocusedFormatByProduct] = useState({});
    const [viewMode, setViewMode] = useState('capacity');
    const [showStandardized, setShowStandardized] = useState(false);
    const [finishedKegs, setFinishedKegs] = useState({});
    const [formatAllocations, setFormatAllocations] = useState({});
    const [targetAllocations, setTargetAllocations] = useState({});
    const [enabledFormats, setEnabledFormats] = useState({});
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [planName, setPlanName] = useState('');
    const [planStartDate, setPlanStartDate] = useState('');
    const [planEndDate, setPlanEndDate] = useState('');
    const [planNotes, setPlanNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
    const [hasAutoOpenedProduct, setHasAutoOpenedProduct] = useState(false);
    const [createdPlan, setCreatedPlan] = useState(null);

    const { data: productionCapacityData } = useQuery({
        queryKey: ['production-capacity', selectedProductIds],
        queryFn: () => productApi.getProductionCapacity(selectedProductIds, true),
        enabled: selectedProductIds.length > 0,
        placeholderData: (previousData) => previousData,
        staleTime: 10000
    });

    useEffect(() => {
        if (!productionCapacityData) return;

        const { processedData, inventory } = buildInitialScenarioData(productionCapacityData);
        const allProductIds = processedData.products_with_capacity.map(product => product.product_id);

        setOriginalData(processedData);
        setScenarioCapacity(processedData);
        setScenarioInventory(inventory);
        setSelectedProducts(allProductIds);
        setActiveProductId(allProductIds[0] || null);
        setActiveFormatFamilyByProduct({});
        setFocusedFormatByProduct({});
        setHasAutoOpenedProduct(false);
    }, [productionCapacityData]);

    const displayedProducts = useMemo(() => {
        if (!scenarioCapacity) return [];

        return scenarioCapacity.products_with_capacity.filter(product =>
            selectedProducts.includes(product.product_id)
        );
    }, [scenarioCapacity, selectedProducts]);

    useEffect(() => {
        if (hasAutoOpenedProduct) return;
        if (!scenarioCapacity || displayedProducts.length === 0) return;
        if (activeProductId) return;

        setActiveProductId(displayedProducts[0].product_id);
        setHasAutoOpenedProduct(true);
    }, [activeProductId, displayedProducts, hasAutoOpenedProduct, scenarioCapacity]);

    useEffect(() => {
        if (displayedProducts.length === 0) return;

        const activeStillVisible = displayedProducts.some(product => product.product_id === activeProductId);
        if (!activeStillVisible) {
            setActiveProductId(displayedProducts[0].product_id);
        }
    }, [activeProductId, displayedProducts]);

    const getAvailableKegsForProduct = useCallback((product) => {
        return getAvailableConcentrateKegs(product, finishedKegs);
    }, [finishedKegs]);

    const getAllocatedKegsForProduct = useCallback((productId) => {
        return getTotalAllocatedKegs(formatAllocations, productId);
    }, [formatAllocations]);

    const getRevenueForProduct = useCallback((productId) => {
        if (viewMode === 'target') {
            return getProductTargetRevenue(targetAllocations, productId);
        }

        return getProductTotalRevenue(formatAllocations, productId);
    }, [formatAllocations, targetAllocations, viewMode]);

    const revenueTotals = useMemo(() => {
        return displayedProducts.reduce((totals, product) => {
            const revenue = viewMode === 'target'
                ? getProductTargetRevenue(targetAllocations, product.product_id)
                : getProductTotalRevenue(formatAllocations, product.product_id);

            return {
                ws: totals.ws + revenue.ws,
                retail: totals.retail + revenue.retail
            };
        }, { ws: 0, retail: 0 });
    }, [displayedProducts, formatAllocations, targetAllocations, viewMode]);

    const handleInventoryChange = useCallback((ingredientId, newQty) => {
        const updated = {
            ...scenarioInventory,
            [ingredientId]: parseFloat(newQty) || 0
        };
        const updatedCapacity = recalculateScenarioCapacity(originalData, updated);

        setScenarioInventory(updated);
        if (updatedCapacity) {
            setScenarioCapacity(updatedCapacity);
        }
    }, [originalData, scenarioInventory]);

    const toggleProductSelection = useCallback((productId) => {
        setSelectedProducts(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    }, []);

    const setActiveProduct = useCallback((productId) => {
        setActiveProductId(productId);
    }, []);

    const setActiveFormatFamily = useCallback((productId, familyId) => {
        setActiveFormatFamilyByProduct(prev => ({
            ...prev,
            [productId]: familyId
        }));
    }, []);

    const setFocusedFormat = useCallback((productId, formatId) => {
        const format = FORMAT_DEFINITIONS[formatId];
        if (!format) return;

        setFocusedFormatByProduct(prev => ({
            ...prev,
            [productId]: formatId
        }));

        setActiveFormatFamilyByProduct(prev => ({
            ...prev,
            [productId]: format.is_rtd ? 'rtd' : 'concentrate'
        }));
    }, []);

    const handleFinishedKegsChange = useCallback((productId, kegs) => {
        setFinishedKegs(prev => ({
            ...prev,
            [productId]: parseFloat(kegs) || 0
        }));
    }, []);

    const toggleFormatEnabled = useCallback((productId, formatId) => {
        setEnabledFormats(prev => {
            const productFormats = prev[productId] || {};
            const isCurrentlyEnabled = Boolean(productFormats[formatId]);

            if (isCurrentlyEnabled) {
                setFormatAllocations(currentAllocations => {
                    const productAllocations = { ...(currentAllocations[productId] || {}) };
                    delete productAllocations[formatId];

                    return {
                        ...currentAllocations,
                        [productId]: productAllocations
                    };
                });
            }

            return {
                ...prev,
                [productId]: {
                    ...productFormats,
                    [formatId]: !isCurrentlyEnabled
                }
            };
        });
    }, []);

    const handleFormatAllocation = useCallback((productId, formatId, kegs) => {
        if (!scenarioCapacity) return;

        const product = scenarioCapacity.products_with_capacity.find(item => item.product_id === productId);
        if (!product) return;

        const kegValue = parseFloat(kegs) || 0;
        const availableKegs = getAvailableConcentrateKegs(product, finishedKegs);
        const maxAllowedForThisFormat = getRemainingKegsForFormat(formatAllocations, productId, formatId, availableKegs);
        const cappedKegs = Math.min(kegValue, maxAllowedForThisFormat);

        setFormatAllocations(prev => ({
            ...prev,
            [productId]: {
                ...(prev[productId] || {}),
                [formatId]: cappedKegs
            }
        }));
    }, [finishedKegs, formatAllocations, scenarioCapacity]);

    const handleTargetAllocation = useCallback((productId, formatId, targetUnits) => {
        setTargetAllocations(prev => ({
            ...prev,
            [productId]: {
                ...(prev[productId] || {}),
                [formatId]: parseFloat(targetUnits) || 0
            }
        }));
    }, []);

    const resetScenario = useCallback(() => {
        if (!originalData) return;

        const inventory = buildInventoryFromScenarioData(originalData);
        const allProductIds = originalData.products_with_capacity.map(product => product.product_id);

        setScenarioInventory(inventory);
        setScenarioCapacity(originalData);
        setFinishedKegs({});
        setFormatAllocations({});
        setTargetAllocations({});
        setEnabledFormats({});
        setSelectedProducts(allProductIds);
        setActiveProductId(allProductIds[0] || null);
        setActiveFormatFamilyByProduct({});
        setFocusedFormatByProduct({});
        setHasAutoOpenedProduct(false);
    }, [originalData]);

    const openPlanModal = useCallback(() => {
        // Capacity mode's quantity field shows a pre-filled default (the
        // remaining producible amount) whenever nothing's been explicitly
        // typed. That default only lives in the UI's calculation until now —
        // commit it into real state here so the plan preview and the actual
        // submitted payload match exactly what was shown on screen.
        if (viewMode === 'capacity' && scenarioCapacity) {
            setFormatAllocations(prevAllocations => {
                const updated = { ...prevAllocations };

                displayedProducts.forEach(product => {
                    const enabledFormatIds = Object.entries(enabledFormats[product.product_id] || {})
                        .filter(([, isEnabled]) => isEnabled)
                        .map(([formatId]) => formatId);

                    if (enabledFormatIds.length === 0) return;

                    const availableKegsForProduct = getAvailableConcentrateKegs(product, finishedKegs);
                    let productAllocations = { ...(updated[product.product_id] || {}) };

                    enabledFormatIds.forEach(formatId => {
                        if (productAllocations[formatId] !== undefined) return;

                        const remaining = getRemainingKegsForFormat(
                            { [product.product_id]: productAllocations },
                            product.product_id,
                            formatId,
                            availableKegsForProduct
                        );

                        if (remaining > 0) {
                            productAllocations = { ...productAllocations, [formatId]: remaining };
                        }
                    });

                    updated[product.product_id] = productAllocations;
                });

                return updated;
            });
        }

        const today = new Date();
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);

        setPlanStartDate(today.toISOString().split('T')[0]);
        setPlanEndDate(nextWeek.toISOString().split('T')[0]);
        setPlanName(`Production Plan - ${today.toLocaleDateString()}`);
        setPlanNotes('');
        setShowPlanModal(true);
        setSubmitMessage({ type: '', text: '' });
    }, [displayedProducts, enabledFormats, finishedKegs, scenarioCapacity, viewMode]);

    const closePlanModal = useCallback(() => {
        setShowPlanModal(false);
    }, []);

    const createProductionPlan = useCallback(async () => {
        if (!planName.trim()) {
            setSubmitMessage({ type: 'error', text: 'Please enter a plan name' });
            return;
        }

        if (!planStartDate || !planEndDate) {
            setSubmitMessage({ type: 'error', text: 'Please select start and end dates' });
            return;
        }

        if (displayedProducts.length === 0) {
            setSubmitMessage({ type: 'error', text: 'Please select at least one product' });
            return;
        }

        const overAllocatedProducts = displayedProducts.filter(product => {
            const availableKegs = getAvailableConcentrateKegs(product, finishedKegs);
            const requiredKegs = viewMode === 'target'
                ? getTargetKegsNeeded(targetAllocations, product.product_id)
                : getTotalAllocatedKegs(formatAllocations, product.product_id);

            return requiredKegs > availableKegs;
        });

        if (overAllocatedProducts.length > 0) {
            const productNames = overAllocatedProducts.map(product => product.product_name).join(', ');
            setSubmitMessage({
                type: 'error',
                text: `Cannot create plan: Over-allocated concentrate kegs for ${productNames}. Please adjust allocations.`
            });
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });

        try {
            const productionRuns = viewMode === 'target'
                ? buildTargetProductionRunsPayload({
                    displayedProducts,
                    targetAllocations,
                    planStartDate
                })
                : buildProductionRunsPayload({
                    displayedProducts,
                    formatAllocations,
                    planStartDate
                });

            if (productionRuns.length === 0) {
                setSubmitMessage({
                    type: 'error',
                    text: viewMode === 'target'
                        ? 'No target quantities have been entered. Please enter target quantities for at least one format.'
                        : 'No products have been allocated. Please allocate concentrate kegs to formats.'
                });
                setIsSubmitting(false);
                return;
            }

            const payload = {
                name: planName,
                planned_start_date: new Date(planStartDate).toISOString(),
                planned_end_date: new Date(planEndDate).toISOString(),
                notes: planNotes,
                production_runs: productionRuns
            };

            const response = await api.post('/api/v1/production/plans', payload);

            setCreatedPlan({
                id: response.data.id || 'Created',
                name: planName,
                startDate: planStartDate,
                endDate: planEndDate,
                productsCount: displayedProducts.length
            });
            setShowPlanModal(false);
            setSubmitMessage({ type: '', text: '' });
        } catch (error) {
            setSubmitMessage({
                type: 'error',
                text: `Failed to create plan: ${describePlanCreationError(error)}`
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [
        displayedProducts,
        finishedKegs,
        formatAllocations,
        planEndDate,
        planName,
        planNotes,
        planStartDate,
        targetAllocations,
        viewMode
    ]);

    return {
        selectedProductIds,
        scenarioCapacity,
        scenarioInventory,
        selectedProducts,
        activeProductId,
        activeFormatFamilyByProduct,
        focusedFormatByProduct,
        viewMode,
        showStandardized,
        finishedKegs,
        formatAllocations,
        targetAllocations,
        enabledFormats,
        displayedProducts,
        revenueTotals,
        planModal: {
            showPlanModal,
            planName,
            planStartDate,
            planEndDate,
            planNotes,
            isSubmitting,
            submitMessage,
            createdPlan
        },
        actions: {
            setActiveProduct,
            setActiveFormatFamily,
            setFocusedFormat,
            setShowStandardized,
            setViewMode,
            setPlanName,
            setPlanStartDate,
            setPlanEndDate,
            setPlanNotes,
            closePlanModal,
            createProductionPlan,
            getAllocatedKegsForProduct,
            getAvailableKegsForProduct,
            getRevenueForProduct,
            handleFinishedKegsChange,
            handleFormatAllocation,
            handleInventoryChange,
            handleTargetAllocation,
            openPlanModal,
            resetScenario,
            toggleFormatEnabled,
            toggleProductSelection,
            setCreatedPlan
        }
    };
};
